import { nonOptimistic } from '@rocketlane/state-db';
import { ProjectId } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { queryClient } from 'queries/client';
import { getProjectSprintsQueryKey } from 'queries/common';
import { logger } from 'services';
import { negativeUid } from 'utils/uid';
import { moveTaskToPhase } from './mutations';
import { mapTaskChangesToPayload } from './payload';
import { taskCollection } from './task-collection';
import { notifyTaskCreated } from './task-created-signal';
import { taskFunnel } from './telemetry';
/**
 * Provenance for every push in this module.
 *
 * This file is the WRITE layer: each push here carries the result of a change
 * THIS tab just made server-side, so other tabs need it. Reads live in
 * `fetchers.ts` (default `'fetch'` — silent, since a peer fetches its own data)
 * and socket handlers tag `'ws'` (silent, since every tab receives the event
 * independently). Getting this wrong is invisible: an untagged write simply
 * never reaches other tabs. See `PushSource` in @rocketlane/state-db.
 */ const MUTATION_PUSH = {
    source: 'mutation'
};
/** Narrows an untyped API response to `Task[]` before pushing. */ const isTaskArray = (value)=>Array.isArray(value);
/** Narrows an untyped API response to a single `Task` before pushing. */ const isTask = (value)=>typeof value === 'object' && value !== null && typeof value.taskId === 'number';
/**
 * Action helpers for cases that are not a single-field task update:
 * bulk edits, optimistic create, WS sync, dependencies, ratings.
 *
 * Single-field updates / deletes go through `taskCollection.update` /
 * `.delete` directly (or the `useCollectionMutation` hook).
 */ /**
 * Optimistically create a task. Uses a temporary negative id (matching the
 * existing `negativeUid()` convention); the collection swaps it for the
 * server id once `createTask` resolves.
 */ /**
 * Optimistic task field op-update from a UI surface (task drawer sections,
 * pickers). Wraps `taskCollection.update` with funnel telemetry: emits
 * `task.field_update` on apply and `task.field_update.error` if the server
 * rejects and the overlay rolls back. Reflects on whichever page is mounted
 * (plan / list) since both read this collection. Use this instead of a raw
 * `taskCollection.update` for user-driven single-task edits.
 */ export const optimisticTaskFieldUpdate = (taskId, payload, kind = 'field')=>{
    taskFunnel.fieldUpdate({
        taskId,
        fields: Object.keys(payload !== null && payload !== void 0 ? payload : {}),
        kind
    });
    const tx = taskCollection.update(taskId, payload);
    tx.isPersisted.catch(()=>taskFunnel.fieldUpdateError({
            taskId,
            kind
        }));
    return tx;
};
export const createTaskOptimistic = (projectId, draft)=>{
    var _draft_project, _ref;
    var _draft_parentTaskObject;
    const tempTask = {
        ...draft,
        taskId: negativeUid(),
        project: {
            ...(_draft_project = draft.project) !== null && _draft_project !== void 0 ? _draft_project : {},
            projectId
        }
    };
    // Funnel: a parented draft is a subtask add. Optimistic insert reflects on
    // whichever page is mounted (plan / list) since both read this collection.
    const parentTaskId = (_ref = (_draft_parentTaskObject = draft.parentTaskObject) === null || _draft_parentTaskObject === void 0 ? void 0 : _draft_parentTaskObject.taskId) !== null && _ref !== void 0 ? _ref : draft.parentTask;
    if (parentTaskId != null) {
        taskFunnel.subtaskAdd({
            parentTaskId,
            optimistic: true
        });
    }
    return taskCollection.insert(tempTask);
};
/**
 * Bulk update many tasks (known ids) in a single API call, optimistically.
 * All changes roll back together if the bulk call fails.
 *
 * The bulk endpoint returns no bodies; visible state stays optimistic until a
 * WS refetch reconciles it (consistent with the WS=refetch decision).
 */ export const bulkUpdateTasks = (updates, options = {})=>taskCollection.batch((recorder)=>{
        updates.forEach(({ taskId, changes })=>recorder.update(taskId, changes));
    }, async ({ mutations })=>{
        const tasks = mutations.map((m)=>{
            var _m_changes;
            return {
                taskId: m.id,
                ...mapTaskChangesToPayload((_m_changes = m.changes) !== null && _m_changes !== void 0 ? _m_changes : {})
            };
        });
        await TaskAPI.bulkUpdateTask({
            tasks
        }, {
            isAllTask: options.isAllTask
        });
    });
/** Refetch a single task from the server and push it into the collection. */ export const syncTaskFromServer = async (projectId, taskId)=>{
    const task = await TaskAPI.getTask(projectId, taskId);
    taskCollection.push([
        task
    ], MUTATION_PUSH);
};
/** Refetch several tasks and push them in one batch. */ export const syncTasksFromServer = async (projectId, taskIds)=>{
    const tasks = await Promise.all(taskIds.map((taskId)=>TaskAPI.getTask(projectId, taskId)));
    taskCollection.push(tasks, MUTATION_PUSH);
};
/**
 * Imperative persistence wrappers — call the endpoint, mirror the server
 * response into the collection, and RETURN the result. Use these where the
 * caller needs the updated `Task` synchronously (sagas that `yield`, React
 * Query mutationFns, components that read the response) and manages its own
 * optimistic state. Fire-and-forget optimistic mutations should use
 * `taskCollection.update/insert/delete` instead.
 */ export const createTaskRecord = nonOptimistic({
    name: 'task.createRecord',
    because: 'every caller needs the server-assigned taskId — to navigate to the new ' + 'task, to map a temp id to the real one, to seed a space-tab row. An ' + 'optimistic `insert()` returns a Transaction, and `Transaction` exposes ' + 'neither the committed entity nor the id the temp key was swapped for, so ' + 'there is nothing to return. Callers that do NOT need the id use ' + 'createTaskOptimistic, which is the overlay path',
    run: async (...args)=>{
        const task = await TaskAPI.createTask(...args);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        return task;
    }
});
export const updateTaskRecord = nonOptimistic({
    name: 'task.updateRecord',
    because: 'its contract is "resolve to the SERVER task", and the overlay path cannot ' + 'honour it: `isTaskPaneMiniEnabled()` is unconditionally true, so ' + '`routeUpdate` sends field edits to the mini endpoint and deliberately ' + 'returns undefined (the mini response is unreliable — it drops assignees ' + 'and returns a stale taskEffortSetting). `update()` would therefore commit ' + "the optimistic merge and hand callers nothing, and callers read the " + 'response: the global-search row reads server-derived status, the CP drawer ' + 'reads back `fields`, use-update-tasks pushes the returned task. It also ' + 'takes an `actor`, which becomes the X-Actor header the originating surface ' + 'uses to ignore its own WS echo; `routeUpdate` has no way to thread it, and ' + 'dropping it causes a refetch/flicker loop. Callers wanting an overlay ' + 'already use optimisticTaskFieldUpdate',
    run: async (...args)=>{
        const task = await TaskAPI.updateTask(...args);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        return task;
    }
});
export const updateTaskMiniRecord = nonOptimistic({
    name: 'task.updateMiniRecord',
    because: 'same as task.updateRecord — and currently unreferenced outside tests ' + '(`TaskAPI.updateTaskMini` is reached via routeUpdate). Kept for parity ' + 'rather than given an overlay it has no caller to benefit',
    run: async (...args)=>{
        const task = await TaskAPI.updateTaskMini(...args);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        return task;
    }
});
/**
 * Optimistic + returns the entity (pattern P6). Delegates to the declared
 * `task.moveToPhase` mutation, which owns the overlay, the swimlane
 * `entityOrder` patch and its undo, and the lane-refresh signals peers need.
 */ export const updatePhaseRecord = async (taskId, payload)=>{
    if (taskCollection.get(taskId) === undefined) {
        var _task_project;
        // Not in the collection — no overlay base, so there is nothing to be
        // optimistic about and no local surface to keep in step. Direct write.
        const task = await TaskAPI.updatePhase(taskId, payload);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        // This branch skips `update()`, so the collection's onUpdate — which owns
        // the sprints-cache invalidation — never runs for it.
        const projectId = (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
        if (projectId != null) {
            queryClient.invalidateQueries(getProjectSprintsQueryKey(ProjectId(projectId)));
        }
        return task;
    }
    return moveTaskToPhase.run({
        taskId,
        payload
    });
};
/**
 * Optimistic + returns the entity (pattern P6, as `updatePhaseRecord`).
 *
 * `routeUpdate` already sends a `private` patch to `TaskAPI.updatePrivacy`, so
 * the overlay and the endpoint agree by construction — this used to call the
 * endpoint directly and push the response, which meant the padlock only flipped
 * after the round-trip, here and in every other tab.
 */ export const updatePrivacyRecord = async (taskId, data)=>{
    if (taskCollection.get(taskId) === undefined) {
        // No overlay base — nothing to be optimistic about. Direct write.
        const task = await TaskAPI.updatePrivacy(taskId, data);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        return task;
    }
    const tx = taskCollection.update(taskId, {
        private: data.private
    });
    await tx.isPersisted; // rejects on server error — overlay already rolled back
    return taskCollection.get(taskId);
};
export const updateSubTaskOrderRecord = (...args)=>TaskAPI.updateSubTaskOrder(...args);
/**
 * Imperative bulk update — forwards the full `BulkTaskPayload` (including
 * `operationName` / `bulkShiftDates`) to the endpoint. Use where the caller
 * manages its own optimistic state; for AgGrid-style optimistic bulk edits use
 * `bulkUpdateTasks` (which applies an optimistic overlay) instead.
 */ export const bulkUpdateTasksRecord = (...args)=>TaskAPI.bulkUpdateTask(...args);
/**
 * Delete a task. Optimistic: the row disappears here and in every peer tab
 * immediately, and comes back if the server refuses.
 *
 * `onDelete` already routes to `TaskAPI.deleteTask` with the project id read off
 * the stored task, so the overlay path and the endpoint agree. The direct-write
 * fallback covers a task this tab never loaded (nothing to hide) and the `actor`
 * argument, which the collection handler does not thread through.
 */ export const deleteTaskRecord = async (...args)=>{
    const [projectId, taskId, actor] = args;
    if (actor !== undefined || taskCollection.get(taskId) === undefined) {
        await TaskAPI.deleteTask(...args);
        // `source: 'mutation'` — the default is `'ws'`, which the sync adapter
        // deliberately ignores because every tab receives a socket event itself.
        // This is a LOCAL deletion, so a peer without that subscription would keep
        // showing the task.
        taskCollection.externalDelete(taskId, {
            source: 'mutation'
        });
        return;
    }
    // Pass the caller's project id through rather than letting `onDelete` re-derive
    // it — a light-payload copy may have no `project` at all.
    await taskCollection.delete(taskId, {
        metadata: {
            projectId
        }
    }).isPersisted;
};
/**
 * Submit a CSAT rating. Not a task-field mutation (the endpoint returns a
 * boolean), so it runs outside the optimistic system; refetch reconciles the
 * task's `csatRatings`.
 */ export const submitTaskRating = async (taskId, data, projectId)=>{
    var _taskCollection_get_project, _taskCollection_get;
    await TaskAPI.addRating(taskId, data);
    const resolvedProjectId = projectId !== null && projectId !== void 0 ? projectId : (_taskCollection_get = taskCollection.get(taskId)) === null || _taskCollection_get === void 0 ? void 0 : (_taskCollection_get_project = _taskCollection_get.project) === null || _taskCollection_get_project === void 0 ? void 0 : _taskCollection_get_project.projectId;
    if (resolvedProjectId != null) {
        await syncTaskFromServer(resolvedProjectId, taskId);
    }
};
/**
 * Duplicate a task N times via the dedicated endpoint. Returns no bodies
 * (`Promise<void>`); the resulting tasks arrive via a WS refetch, so there is
 * nothing to push here.
 */ export const duplicateTask = (...args)=>TaskAPI.duplicateTask(...args);
/**
 * The server builds the create response before the task→sprint mapping is
 * written (an `@AfterReturning` AOP action does that), so it never comes back
 * carrying `currentSprint` even when the request asked for one. Restore it
 * from the request payload, here — where the payload and the response meet —
 * so the collection and the created-signal both see the sprint the caller
 * asked for. `sprintName` is not in the payload and stays absent; every
 * consumer optional-chains it.
 */ const withRequestedSprint = (task, requestedSprint)=>!task.currentSprint && (requestedSprint === null || requestedSprint === void 0 ? void 0 : requestedSprint.sprintId) ? {
        ...task,
        currentSprint: requestedSprint
    } : task;
/** Create a task from a form submission → push the returned task. */ export const createTaskWithForm = nonOptimistic({
    name: 'task.createWithForm',
    because: 'same id problem as task.createRecord, plus the response needs ' + 'post-processing the client cannot predict: `withRequestedSprint` repairs a ' + 'currentSprint the server omits because its AOP mapping runs after the ' + 'response is built, and `notifyTaskCreated` broadcasts the REAL id',
    run: async (...args)=>{
        var _args__data, _args_;
        const task = withRequestedSprint(await TaskAPI.createTaskWithForm(...args), (_args_ = args[0]) === null || _args_ === void 0 ? void 0 : (_args__data = _args_.data) === null || _args__data === void 0 ? void 0 : _args__data.currentSprint);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        notifyTaskCreated(task);
        return task;
    }
});
/** Idempotent create (X-Request-Id) → push the returned task. */ export const createTaskWithRequestId = nonOptimistic({
    name: 'task.createWithRequestId',
    because: 'same as task.createWithForm. The X-Request-Id header exists to make the ' + 'create idempotent under retry, which is a server-side dedupe the client ' + 'cannot mirror in an overlay',
    run: async (...args)=>{
        var _args_;
        const task = withRequestedSprint(await TaskAPI.createTaskWithRequestId(...args), (_args_ = args[1]) === null || _args_ === void 0 ? void 0 : _args_.currentSprint);
        taskCollection.push([
            task
        ], MUTATION_PUSH);
        notifyTaskCreated(task);
        return task;
    }
});
/** Add tasks to a project from templates → push the returned tasks. */ export const addTaskTemplatesToProject = nonOptimistic({
    name: 'task.addFromTemplates',
    because: 'the server expands each template into a set of tasks — count, ids, ' + 'phases and subtask nesting are all decided server-side, so there is no ' + 'entity the client could show before the response arrives',
    run: async (...args)=>{
        const result = await TaskAPI.addTaskTemplatesToProject(...args);
        taskCollection.push(result, MUTATION_PUSH);
        return result;
    }
});
/**
 * Bulk-create top-level tasks under a project. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleTasks = nonOptimistic({
    name: 'task.addMultiple',
    because: 'a bulk create: the server assigns every id, so a temp-id overlay would ' + 'need one placeholder per row and a swap for each — and the endpoint ' + 'return is untyped, so even the count is not guaranteed up front',
    run: async (...args)=>{
        const result = await TaskAPI.addMultipleTasks(...args);
        if (isTaskArray(result)) {
            taskCollection.push(result, MUTATION_PUSH);
        }
        return result;
    }
});
/**
 * Bulk-create subtasks under a parent task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task[]`.
 */ export const addMultipleSubtasks = nonOptimistic({
    name: 'task.addMultipleSubtasks',
    because: 'a bulk create under a parent — same reason as task.addMultiple: the ' + 'server assigns the ids and the return is untyped. Single subtask adds ' + 'DO have an overlay, via createTaskOptimistic',
    run: async (...args)=>{
        const [parentTaskId] = args;
        try {
            const result = await TaskAPI.addMultipleSubtasks(...args);
            if (isTaskArray(result)) {
                taskCollection.push(result, MUTATION_PUSH);
            }
            if (typeof parentTaskId === 'number') {
                taskFunnel.subtaskAdd({
                    parentTaskId,
                    optimistic: false
                });
            }
            return result;
        } catch (error) {
            if (typeof parentTaskId === 'number') {
                taskFunnel.subtaskAddError({
                    parentTaskId
                });
            }
            throw error;
        }
    }
});
/**
 * Merge only the `dependentTasks` relation off a freshly-fetched task into
 * whatever's currently cached — never the whole snapshot. Both
 * `createDependentTask` and `updateDependentTasks` fetch the task server-side
 * (`getTaskDetails`) *before* their `@AfterReturning` AOP date-shift cascade
 * runs, so the response always carries pre-cascade dates. A full-task push
 * (`push()` always replaces — `isNewer` is unconditionally `true`) would
 * clobber whatever dates the client just computed optimistically for the
 * same task, which is exactly the "dates revert a moment after the
 * dependency is created" bug this avoids.
 */ const mergeDependentTasksIntoCollection = (fresh)=>{
    const current = taskCollection.get(fresh.taskId);
    if (!current) {
        return;
    }
    taskCollection.push([
        {
            ...current,
            dependentTasks: fresh.dependentTasks
        }
    ], MUTATION_PUSH);
};
/**
 * Create a dependency between tasks. The `add-dependency` endpoint actually
 * returns an echo of the request body (`TaskToDependentTaskModel`), not a
 * `Task` — `TaskAPI.createDependentTask`'s declared return type is a lie.
 * Refetch the real task to reconcile `dependentTasks` (see
 * `mergeDependentTasksIntoCollection` for why only that field is merged).
 *
 * The refetch is best-effort: the dependency was already created server-side
 * by the call above, and callers (the gantt drag saga has no try/catch; the
 * task-pane caller has no `.catch`) would otherwise have an uncaught
 * rejection abort their whole follow-up flow — or, worse, crash the saga
 * middleware — over a reconciliation fetch that isn't on the critical path.
 */ export const createDependentTask = async (...args)=>{
    const [projectId, taskId] = args;
    const result = await TaskAPI.createDependentTask(...args);
    try {
        const fresh = await TaskAPI.getTask(projectId, taskId);
        mergeDependentTasksIntoCollection(fresh);
    } catch (error) {
        logger.error('createDependentTask: failed to refetch task', {
            error,
            projectId,
            taskId
        });
    }
    return result;
};
/**
 * Remove a dependency between tasks. Returns a boolean; callers should refetch
 * the affected task(s) via `syncTasksFromServer` to reconcile state.
 */ export const removeDependentTask = (...args)=>TaskAPI.removeDependentTask(...args);
/**
 * Update a task's dependent tasks. Returns the updated `Task`, which — per
 * `mergeDependentTasksIntoCollection` — only has its `dependentTasks` merged
 * into the collection; the returned dates are pre-cascade and must not
 * overwrite the client-computed optimistic dates. Callers should still
 * refetch the other affected task(s) via `syncTasksFromServer` since the
 * response only carries the directly-edited task.
 */ export const updateDependentTasks = async (...args)=>{
    const result = await TaskAPI.updateDependentTasks(...args);
    mergeDependentTasksIntoCollection(result);
    return result;
};
/**
 * Promote a subtask back to a top-level task. The endpoint return is untyped
 * (`Promise<unknown>`); push only when it resolves to a `Task`.
 */ export const removeAsSubtask = nonOptimistic({
    name: 'task.removeAsSubtask',
    because: 'no overlay can reach this endpoint: `routeUpdate` has no branch for ' + 'clearing a parent, so `update(id, { parentTask: null })` would route to the ' + 'generic mini endpoint instead of /remove-as-subtask. Promotion also moves ' + 'the task between the byParent index buckets and rewrites the parent\'s ' + 'subTaskOrder, and the caller re-pushes a pre-image on failure — a rollback ' + 'the collection does not model. Add the route before making this optimistic',
    run: async (...args)=>{
        const result = await TaskAPI.removeAsSubtask(...args);
        if (isTask(result)) {
            taskCollection.push([
                result
            ], MUTATION_PUSH);
        }
        return result;
    }
});
/** Delete a task attachment. No `Task` entity returned — just forward. */ export const deleteTaskAttachment = (...args)=>TaskAPI.deleteTaskAttachment(...args);
/**
 * Bulk-update tasks for a select-all operation. The endpoint returns no per-id
 * bodies, so there is nothing to push; visible state stays optimistic until a
 * WS refetch reconciles it. Just forward.
 */ export const bulkUpdateAllTasks = (...args)=>TaskAPI.bulkUpdateAllTasks(...args);
/**
 * Delete tasks for a select-all operation via the filter-scoped endpoint. The
 * server resolves the full matching set (loaded + unloaded pages) and returns
 * the deleted top-level ids; we remove those from the collection so any
 * collection-driven view reflects the deletion immediately, and the grid is
 * reconciled by the following WS refetch. Fixes the AG-Grid ~300-row
 * cached-selection cap on the client.
 */ export const bulkDeleteAllTasks = nonOptimistic({
    name: 'task.bulkDeleteAll',
    because: 'the filter, not a list of ids, is the argument: the server resolves ' + 'which tasks match across pages this tab never loaded, and returns the ' + 'ids it deleted. There is no local set to hide up front — hiding only the ' + 'loaded matches would under-report the deletion the user asked for',
    run: async (...args)=>{
        const response = await TaskAPI.bulkDeleteAllTasks(...args);
        const deletedIds = response === null || response === void 0 ? void 0 : response.taskIds;
        (deletedIds !== null && deletedIds !== void 0 ? deletedIds : []).forEach((taskId)=>{
            // Local deletion — see `deleteTaskRecord`.
            taskCollection.externalDelete(taskId, {
                source: 'mutation'
            });
        });
    }
});
/**
 * Apply a dependency date cascade to the tasks this tab has loaded.
 *
 * The server owns the cascade: a date edit sends ONLY the edited task's dates
 * (see `handleChange` in the dependency-conflict modal), and the backend
 * shifts everything downstream. `getDateShiftsForEntity` computes the same
 * shifts client-side purely to render the confirmation table — the numbers
 * were then thrown away, so dependents already on screen kept their old dates
 * until something happened to refetch them.
 *
 * Those numbers are good enough to show the user in a confirmation dialog, so
 * they are good enough to display. Write them.
 *
 * Deliberately scoped to loaded tasks. A dependent on a page this tab has
 * never fetched needs nothing: it arrives already shifted whenever its page
 * loads, because the server got there first.
 *
 * `source: 'local'` — this is a client-computed prediction of what the server
 * is doing, not a confirmed result. Other tabs derive their own from the
 * socket events for the same change; broadcasting ours would hand them a guess
 * they cannot retract if the parent write fails.
 *
 * Returns an undo, so the caller can tie the cascade to the parent
 * transaction: if the edit that triggered it is rejected, the shift never
 * happened either.
 */ export const applyDependentDateShifts = (updates, excludeTaskId)=>{
    const before = [];
    const after = [];
    Object.entries(updates !== null && updates !== void 0 ? updates : {}).forEach(([key, update])=>{
        var _ref, _ref1;
        const taskId = Number(key);
        if (!Number.isFinite(taskId) || taskId === excludeTaskId) {
            return;
        }
        const existing = taskCollection.get(taskId);
        if (existing === undefined) {
            return;
        }
        const startDate = (_ref = update === null || update === void 0 ? void 0 : update.startDate) !== null && _ref !== void 0 ? _ref : existing.startDate;
        const dueDate = (_ref1 = update === null || update === void 0 ? void 0 : update.dueDate) !== null && _ref1 !== void 0 ? _ref1 : existing.dueDate;
        if (startDate === existing.startDate && dueDate === existing.dueDate) {
            return;
        }
        before.push(existing);
        after.push({
            ...existing,
            startDate,
            dueDate
        });
    });
    if (after.length === 0) {
        return ()=>undefined;
    }
    taskCollection.push(after, {
        source: 'local'
    });
    return ()=>{
        taskCollection.push(before, {
            source: 'local'
        });
    };
};
